import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { PublicAgentChatSessionShareResponse } from "@/lib/api/generated";
import { AgentSessionTranscript } from "@/components/campaign/agent-session-transcript";
import { getPublicAgentChatSessionShareServer } from "@/server/agent-sessions";

export const Route = createFileRoute("/share/tasks/$shareToken")({
  loader: async ({ params }): Promise<{ share: PublicAgentChatSessionShareResponse | null }> => {
    try {
      const share = await getPublicAgentChatSessionShareServer({
        data: { shareToken: params.shareToken },
      });
      return { share };
    } catch {
      return { share: null };
    }
  },
  component: PublicTaskShareRoute,
});

function PublicTaskShareRoute() {
  const { share } = Route.useLoaderData();
  const { t } = useTranslation(["pages", "common"]);

  if (!share) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-white px-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-gray-900">
            {t("common:errors.pageNotFound")}
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            {t("pages:campaigns.agentChat.emptyShare")}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex h-screen min-h-0 flex-col bg-white">
      <header className="shrink-0 border-b border-[#ECEBEB] bg-white px-5 py-4">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("pages:campaigns.agentChat.readonlyBadge")}
            </p>
            <h1 className="mt-1 truncate text-xl font-semibold text-gray-900">{share.title}</h1>
          </div>
        </div>
      </header>
      <AgentSessionTranscript
        history={share.history}
        agentMode={share.agent_mode}
        emptyLabel={t("pages:campaigns.agentChat.emptyShare")}
      />
      <div className="pointer-events-none fixed right-0 bottom-6 left-0 z-30 flex justify-center px-4">
        <a
          href="/"
          className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-full bg-black px-5 text-sm font-medium text-white shadow-[0_12px_32px_rgba(15,23,42,0.18)] transition hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t("pages:campaigns.agentChat.createTask")}
        </a>
      </div>
    </main>
  );
}
